import React, { useEffect, useMemo, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { PageHeader, Card, Section } from "@/components/Primitives";
import FilterBar from "@/components/FilterBar";
import { PencilSimpleLine, FloppyDisk, MagnifyingGlass, Warning, ArrowClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const EDITABLE = [
  { key: "brand", label: "Brand" },
  { key: "category", label: "Category" },
  { key: "description", label: "Description" },
  { key: "item_code", label: "Item code" },
  { key: "salesman_code", label: "Salesman code" },
  { key: "salesman_name", label: "Salesman name" },
  { key: "location_code", label: "Location code" },
  { key: "location_name", label: "Location name" },
  { key: "date", label: "Date (YYYY-MM-DD)" },
  { key: "quantity", label: "Qty", numeric: true },
  { key: "gross_price", label: "Gross Price", numeric: true },
  { key: "discount", label: "Discount", numeric: true },
  { key: "net_value", label: "Net Value", numeric: true },
  { key: "cogs", label: "COGS", numeric: true },
  { key: "gross_profit", label: "Gross Profit", numeric: true },
];

const DataFixer = () => {
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [missingField, setMissingField] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25, common_missing_fields: [] });
  const [summary, setSummary] = useState(null);
  const [editRow, setEditRow] = useState(null); // {id, updates}
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryStr = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.append("q", q);
    if (filters.brand) p.append("brand", filters.brand);
    if (filters.location_code) p.append("location_code", filters.location_code);
    if (filters.salesman_code) p.append("salesman_code", filters.salesman_code);
    if (filters.start_date) p.append("start_date", filters.start_date);
    if (filters.end_date) p.append("end_date", filters.end_date);
    if (onlyMissing && !missingField) p.append("only_missing", "true");
    if (missingField) p.append("missing_field", missingField);
    p.append("page", String(page));
    p.append("page_size", "25");
    return `?${p.toString()}`;
  }, [q, filters, onlyMissing, missingField, page]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/transactions/search${queryStr}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/transactions/missing-summary");
      setSummary(data);
    } catch { /* role restricted */ }
  };

  useEffect(() => { load(); }, [queryStr]);
  useEffect(() => { loadSummary(); }, []);

  const openEdit = (row) => {
    const updates = {};
    EDITABLE.forEach((f) => { updates[f.key] = row[f.key] ?? ""; });
    setEditRow({ id: row.id, orig: row, updates });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setBusy(true);
    try {
      const changed = {};
      EDITABLE.forEach((f) => {
        const orig = editRow.orig[f.key];
        const next = editRow.updates[f.key];
        if (String(orig ?? "") !== String(next ?? "")) changed[f.key] = next === "" ? null : next;
      });
      if (Object.keys(changed).length === 0) { toast.info("No changes to save"); setBusy(false); return; }
      await api.patch(`/transactions/${editRow.id}`, { updates: changed });
      toast.success(`Updated ${Object.keys(changed).length} field(s)`);
      setEditRow(null);
      await load();
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const deleteRow = async (row) => {
    const ok = window.confirm(
      `Delete this transaction permanently?\n\n` +
      `Bill: ${row.bill_no || "—"}\n` +
      `Item: ${row.item_code || "—"} · ${row.description || ""}\n` +
      `Date: ${row.date || "—"}\n` +
      `Net value: ${row.net_value ?? "—"} KWD\n\n` +
      `This cannot be undone (re-uploading the source file would re-insert it).`,
    );
    if (!ok) return;
    try {
      await api.delete(`/transactions/${row.id}`);
      toast.success("Transaction deleted.");
      await load();
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 25)));

  return (
    <div>
      <PageHeader
        eyebrow="Data Fixer"
        title="Fix gaps in the ledger"
        subtitle="Find transactions with missing or wrong details and patch them in-place. Every edit is audited."
      />

      {/* Missing-field summary */}
      {summary && (
        <Card title="Missing-field overview" testId="missing-summary" className="mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="missing-chip-all"
              onClick={() => { setMissingField(""); setOnlyMissing(true); setPage(1); }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                missingField === "" && onlyMissing
                  ? "bg-myb-500 text-white border-myb-500"
                  : "border-myb-200 text-muted-foreground hover:border-myb-500 hover:text-myb-500"
              }`}
            >
              Any missing ({fmtNumber(
                Object.values(summary.per_field).reduce((a, b) => Math.max(a, b), 0)
              )})
            </button>
            {Object.entries(summary.per_field).map(([f, cnt]) => (
              <button
                key={f}
                data-testid={`missing-chip-${f}`}
                onClick={() => { setMissingField(f); setPage(1); }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  missingField === f
                    ? "bg-myb-500 text-white border-myb-500"
                    : "border-myb-200 text-muted-foreground hover:border-myb-500 hover:text-myb-500"
                }`}
              >
                {f.replace(/_/g, " ")} <span className="ml-1 opacity-70">{fmtNumber(cnt)}</span>
              </button>
            ))}
            <button
              data-testid="missing-chip-none"
              onClick={() => { setMissingField(""); setOnlyMissing(false); setPage(1); }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                !onlyMissing && missingField === ""
                  ? "bg-myb-500 text-white border-myb-500"
                  : "border-myb-200 text-muted-foreground hover:border-myb-500 hover:text-myb-500"
              }`}
            >
              Show all
            </button>
            <div className="ml-auto text-xs text-muted-foreground self-center">Out of {fmtNumber(summary.total_transactions)} transactions</div>
          </div>
        </Card>
      )}

      {/* Search + filters */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-myb-500" />
            <input
              data-testid="datafixer-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search bill no / item code / description / salesman…"
              className="w-full bg-white border border-border focus:border-myb-500 rounded-sm pl-9 pr-3 py-2.5 text-sm outline-none"
            />
          </div>
          <button
            onClick={load}
            data-testid="datafixer-refresh"
            className="inline-flex items-center gap-2 text-xs border border-myb-200 hover:border-myb-500 text-muted-foreground hover:text-myb-500 px-4 py-2.5 rounded-sm uppercase tracking-wider font-semibold"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
        <FilterBar value={filters} onChange={(v) => { setFilters(v); setPage(1); }} showSalesman />
      </div>

      {/* Results */}
      <Card testId="datafixer-table">
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">No transactions match these filters.</div>
        ) : (
          <div className="overflow-auto scrollbar-thin max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                <tr className="text-left border-b hairline">
                  {["Date", "Bill #", "Brand", "Item", "Description", "Location", "Salesman", "Net", "Missing", ""].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b hairline hover:bg-myb-50/50" data-testid={`datafixer-row-${r.id}`}>
                    <td className="py-2 px-3 text-muted-foreground font-mono text-xs whitespace-nowrap">{r.date || <Warning size={12} className="inline text-amber-500" />}</td>
                    <td className="py-2 px-3 font-mono text-xs">{r.bill_no || "—"}</td>
                    <td className="py-2 px-3">{r.brand || <span className="text-amber-600">missing</span>}</td>
                    <td className="py-2 px-3 font-mono text-xs">{r.item_code || "—"}</td>
                    <td className="py-2 px-3 max-w-[280px] truncate" title={r.description}>{r.description || <span className="text-amber-600">missing</span>}</td>
                    <td className="py-2 px-3 text-xs">{r.location_name || <span className="text-amber-600">missing</span>}</td>
                    <td className="py-2 px-3 text-xs">{r.salesman_name || <span className="text-amber-600">missing</span>}</td>
                    <td className="py-2 px-3 text-right font-medium">{r.net_value != null ? fmtNumber(r.net_value) : "—"}</td>
                    <td className="py-2 px-3">
                      {r._missing && r._missing.length > 0 ? (
                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">{r._missing.length} fields</span>
                      ) : <span className="text-[10px] text-emerald-600">clean</span>}
                    </td>
                    <td className="py-2 px-3">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(r)}
                          data-testid={`datafixer-edit-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs border border-myb-200 hover:border-myb-500 hover:text-myb-500 text-muted-foreground rounded-sm px-2 py-1 font-medium"
                        >
                          <PencilSimpleLine size={12} /> Edit
                        </button>
                        <button
                          onClick={() => deleteRow(r)}
                          data-testid={`datafixer-delete-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs border border-rose-200 hover:border-rose-500 text-rose-600 hover:text-rose-700 rounded-sm px-2 py-1 font-medium"
                        >
                          <Trash size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t hairline text-xs">
          <div className="text-muted-foreground">
            {fmtNumber(data.total)} results · Page {page} / {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="datafixer-prev"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="border border-myb-200 hover:border-myb-500 hover:text-myb-500 px-3 py-1.5 rounded-sm uppercase tracking-wider font-semibold disabled:opacity-40"
            >
              Prev
            </button>
            <button
              data-testid="datafixer-next"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="border border-myb-200 hover:border-myb-500 hover:text-myb-500 px-3 py-1.5 rounded-sm uppercase tracking-wider font-semibold disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </Card>

      {/* Edit drawer */}
      {editRow && (
        <div className="fixed inset-0 z-40 flex" data-testid="datafixer-edit-panel">
          <div className="flex-1 bg-black/40" onClick={() => !busy && setEditRow(null)} />
          <div className="w-full max-w-lg bg-white h-full overflow-auto scrollbar-thin shadow-2xl border-l hairline">
            <div className="p-5 border-b hairline flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Edit transaction</div>
                <div className="font-display text-xl font-light">{editRow.orig.bill_no || "—"} · {editRow.orig.item_code || ""}</div>
              </div>
              <button data-testid="datafixer-edit-close" onClick={() => setEditRow(null)} className="text-muted-foreground hover:text-myb-500 text-xs uppercase tracking-wider">Close</button>
            </div>
            <div className="p-5 space-y-4">
              {EDITABLE.map((f) => {
                const orig = editRow.orig[f.key];
                const isMissing = orig == null || orig === "";
                return (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2">
                      {f.label}
                      {isMissing && <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5">missing</span>}
                    </label>
                    <input
                      data-testid={`datafixer-field-${f.key}`}
                      type={f.numeric ? "number" : "text"}
                      step={f.numeric ? "0.01" : undefined}
                      value={editRow.updates[f.key] ?? ""}
                      onChange={(e) => setEditRow({
                        ...editRow,
                        updates: { ...editRow.updates, [f.key]: e.target.value },
                      })}
                      className={`mt-1 w-full bg-white border rounded-sm px-3 py-2 text-sm outline-none focus:border-myb-500 ${
                        isMissing ? "border-amber-300 bg-amber-50" : "border-border"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="p-5 border-t hairline sticky bottom-0 bg-white flex gap-2 justify-end">
              <button
                data-testid="datafixer-edit-cancel"
                disabled={busy}
                onClick={() => setEditRow(null)}
                className="text-xs px-4 py-2.5 border border-border rounded-sm uppercase tracking-wider font-semibold text-muted-foreground hover:text-myb-500 hover:border-myb-500"
              >
                Cancel
              </button>
              <button
                data-testid="datafixer-edit-save"
                disabled={busy}
                onClick={saveEdit}
                className="inline-flex items-center gap-2 text-xs px-5 py-2.5 bg-myb-500 text-white hover:bg-myb-600 rounded-sm uppercase tracking-wider font-semibold disabled:opacity-50"
              >
                <FloppyDisk size={14} /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataFixer;
